import React, { useState } from 'react';
import { AiOutlineRight } from 'react-icons/ai';
import { Link } from 'react-router-dom'; // Make sure to import Link from react-router-dom
import './HelpCenter.css';

const helpSections = [
  {
    tab: "Account Setup",
    title: "Account Management",
    articles: [
      { 
        title: "How to Sign Up on Lovely Pair", 
        content: [
          "To get started, click the 'Register' button located on the homepage.",
          "You will need to provide your full name, email address, phone number, and set a secure password.",
          "Once you've completed the form, you'll receive a verification email.",
          "Click the link in the email to verify your account and complete the sign-up process."
        ]
      },
      { 
        title: "Logging into Your Account", 
        content: [
          "After registering, you can log in using your registered email and password.",
          "If you've forgotten your password, simply click on the 'Forgot Password' link on the login page.",
          "You will receive an email with instructions to reset your password securely and regain access to your account."
        ]
      },
      { 
        title: "Filling Basic Details & Preferences", 
        content: [
          "Once logged in, go to 'Edit Profile' to update your personal information.",
          "Add important details such as your age, gender, date of birth, location, education, hobbies, and partner preferences.",
          "The more accurate and complete your profile, the better the experience you'll have on the platform."
        ]
      },
      { 
        title: "Creating a Strong Profile", 
        content: [
          "Your profile is your first impression! Make sure to upload a clear and high-quality profile picture.",
          "You can also add multiple images or a short video reel to showcase your personality.",
          "Write a thoughtful 'About Me' section to introduce yourself honestly and clearly.",
          "Completing all essential fields, such as your professional background, lifestyle choices, and relationship goals, will help attract like-minded individuals."
        ]
      },
      { 
        title: "Managing Family and Lifestyle Information", 
        content: [
          "To complete your profile and give potential matches a fuller picture, fill out the family background section.",
          "Provide lifestyle information such as your occupation, income, height, weight, religion, caste, and any habits like smoking or drinking."
        ]
      },
      { 
        title: "Account Deactivation or Deletion", 
        content: [
          "If you wish to take a break or permanently leave Lovely Pair, you have the option to deactivate or delete your account.",
          "To do this, navigate to 'Account Settings' → 'Privacy.'",
          "Here, you can choose to temporarily deactivate your account or permanently delete your account."
        ]
      }
    ]
  },
  {
    tab: "Matchmaking Support",
    title: "Finding Matches",
    articles: [
      { 
        title: "Setting Partner Preferences", 
        content: [
          "After completing your profile, set your partner preferences by selecting criteria such as age range, education, profession, location, religion, caste, and lifestyle factors.",
          "These preferences will help refine your matches, making it easier to find relevant profiles that align with your goals."
        ]
      },
      { 
        title: "Understanding Match Score System", 
        content: [
          "The Match Score is an algorithmic calculation that reflects how closely a profile aligns with your selected preferences.",
          "Factors such as age, religion, caste, education, and location are considered to generate a compatibility score.",
          "A higher score means a better match and a stronger chance of finding your ideal partner."
        ]
      },
      { 
        title: "Viewing Featured Profiles", 
        content: [
          "Check out the 'Featured Profiles' section to discover carefully selected profiles based on your preferences and platform activity.",
          "These profiles are highlighted because they align closely with your preferences."
        ]
      },
      { 
        title: "Success Stories & Testimonials", 
        content: [
          "Browse through real-life success stories and testimonials from couples who found their perfect match on Lovely Pair.",
          "These stories can offer valuable insights and inspire your own journey toward a meaningful relationship."
        ]
      },
      { 
        title: "Matchmaking Tips & Tricks", 
        content: [
          "To increase your chances of finding a great match, be authentic in your profile, keep an open mind, and ensure that your profile is always up-to-date.",
          "Engage actively with your matches, as consistent communication helps foster better connections."
        ]
      }
    ]
  },
  {
    tab: "Astrologer Assistance",
    title: "Astrological Support",
    articles: [
      { 
        title: "Consulting with Astrologers", 
        content: [
          "Lovely Pair provides the unique opportunity to consult with experienced astrologers who specialize in compatibility analysis and marriage-related auspicious timings.",
          "Our astrologers leverage their expertise in horoscope reading and birth chart analysis to guide you in making informed decisions."
        ]
      },
      { 
        title: "How Astrologers Assist in Matchmaking", 
        content: [
          "Astrologers on Lovely Pair utilize advanced techniques in astrology, including analyzing horoscopes, birth charts, and planetary alignments to suggest the best matches for you.",
          "By considering factors like your zodiac signs, planetary positions, and doshas, astrologers provide insights into your compatibility."
        ]
      },
      { 
        title: "Booking an Astrology Consultation", 
        content: [
          "To book a session with an astrologer, navigate to the 'Astrologer Assistance' section in your dashboard.",
          "Here, you can browse through a list of available astrologers, view their expertise, and select a date and time that fits your schedule."
        ]
      },
      { 
        title: "Frequently Asked Astrology Questions", 
        content: [
          "If you're curious about how astrology plays a role in matchmaking, check out our FAQ section where we answer common questions.",
          "Our astrologers provide explanations on how specific astrological factors, such as star signs, moon signs, and planetary influences, affect relationship compatibility."
        ]
      },
      { 
        title: "Need Further Help?", 
        content: [
          "If you need further assistance beyond your consultation, our support team is always available to help.",
          "Additionally, you can contact your astrologer directly for more personalized guidance or ask questions related to your astrology session."
        ]
      }
    ]
  }
];

const tabs = ["Account Setup", "Matchmaking Support", "Astrologer Assistance"];

const HelpCenter = () => {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setSelectedArticle(null);
  };

  const filteredSections = helpSections.filter(section => section.tab === activeTab);

  return (
    <div className="helpcenter-container">
      
      {/* Header */}
      <div className="helpcenter-header">
        <div className="yellow-circle"></div>
        <div className="helpcenter-header-content">
          <h1>Hi, how can we help you?</h1>
          <div className="helpcenter-search">
            <input
              type="text"
              placeholder="Search for help topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button>Search</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="helpcenter-tabs">
        {tabs.map((tab, index) => (
          <button
            key={index}
            className={`tab-button ${activeTab === tab ? 'active' : ''}`}
            onClick={() => handleTabClick(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Right Side Button */}
      <div className="helpcenter-right-button">
        <Link to="/submitrequest">
          <button className="submit-request-button">
            <AiOutlineRight size={20} /> Submit Request
          </button>
        </Link>
      </div>

      {/* Content Area */}
      <div className="helpcenter-main">
        <div className="helpcenter-articles">
          {filteredSections.map((section, idx) => (
            <ul key={idx}>
              {section.articles
                .filter(article => article.title.toLowerCase().includes(searchQuery.toLowerCase()))
                .map((article, i) => (
                <li 
                  key={i} 
                  onClick={() => setSelectedArticle(article)}
                  className={selectedArticle?.title === article.title ? 'active-article' : ''}
                >
                  {article.title}
                </li>
              ))}
            </ul>
          ))}
        </div>

        <div className="helpcenter-article-content">
          {selectedArticle ? (
            <>
              <h3>{selectedArticle.title}</h3>
              <ul>
                {selectedArticle.content.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
            </>
          ) : (
            <div className="no-article-selected">
              <p>Select a topic to view details</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default HelpCenter;
